import type { Params } from '@angular/router';
import type { Observable } from 'rxjs';
import type { PanelPosition, SelectedEntity } from '@common/types/layout.type';
import type { Uuid } from '@common/types';
import { EDrawerType } from '@common/types/layout.type';

import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { CdkDropListGroup } from '@angular/cdk/drag-drop';
import { AsyncPipe } from '@angular/common';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { distinctUntilChanged, shareReplay, switchMap } from 'rxjs';

import { SpPanelDragDirective } from '@common/directives/panel-drag.directive';

import { blockIsShared, isDefaultBlock, isSharedBlock, isSpecificBlock, isZeroBlock } from '@sites/entity-core';

import type { DenormalizedBlockEntity, DenormalizedDefaultBlockEntity } from '@web/types';

import { SettingsPanelComponent } from '@web/layout/settings-panel/settings-panel.component';

import { RouterFacade } from '@web/store/router/router.facade';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { SettingsFacade } from '@web/store/website/settings/settings.facade';

import { DefaultBlockComponent } from './components/blocks/default-block/default-block.component';
import { SharedBlockComponent } from './components/blocks/shared-block/shared-block.component';
import { SpecificBlockComponent } from './components/blocks/specific-block/specific-block.component';
import { ZeroBlockComponent } from './components/blocks/zero-block/zero-block.component';

@Component({
    selector: 'web-page-content',
    templateUrl: './page-content.component.html',
    styleUrls: ['./page-content.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CdkDropListGroup,
        DefaultBlockComponent,
        SharedBlockComponent,
        SpecificBlockComponent,
        ZeroBlockComponent,
        SettingsPanelComponent,
        SpPanelDragDirective,
        AsyncPipe,
        TranslateModule,
    ],
})
export class PageContentComponent {
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly layoutFacade = inject(LayoutFacade);
    private readonly siteFacade = inject(SiteFacade);
    private readonly settingsFacade = inject(SettingsFacade);
    private readonly routerFacade = inject(RouterFacade);

    public readonly structure$ = this.blocksFacade.structure$;
    public readonly selectedEntity$: Observable<SelectedEntity> = this.layoutFacade.selectedEntity$;
    public readonly isEntityDragging$: Observable<boolean> = this.layoutFacade.isEntityDragging$;
    public readonly id$: Observable<number | Uuid> = this.siteFacade.id$;

    public readonly panelPosition = toSignal(this.layoutFacade.settingsPanelPosition$);

    public readonly showPoweredBy$: Observable<boolean> = this.settingsFacade.showPoweredBy$;

    /**
     * Whether the page being edited is the 404 one — shared and specific blocks
     * are not rendered there, see the template.
     *
     * A field rather than a getter: the template reads it through `async`, and a
     * getter that pipes hands back a new observable on every read, so the pipe
     * tears its subscription down and rebuilds it on every change detection —
     * and `isPage404$(pageId)` builds a fresh, never-hit selector each time it
     * is called.
     */
    public readonly isPage404$: Observable<boolean> = this.routerFacade.routerParams$.pipe(
        switchMap((params: Params) => this.settingsFacade.isPage404$(params.pageId)),
        distinctUntilChanged(),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    public addBlock(): void {
        const blockIndex = 0;
        this.layoutFacade.openDrawer({ drawerType: EDrawerType.block, blockIndex });
    }

    public savePanelPosition(position: PanelPosition): void {
        this.layoutFacade.setSettingsPanelPosition(position.x, position.y);
    }

    /**
     * Which component renders the entity — the guards narrow the block union, so
     * each branch of the template hands its component the subtype it declares.
     *
     * They answer through `blockSubType`/`blockIsShared` rather than reading
     * `entitySubType` and `meta`, which are both produced by the migration: a
     * block whose migration failed is handed back raw — see
     * `WebStructureAdapterService.migrateBlockEntity` — and reading only those two
     * would drop it from the page while leaving it in the store to be saved over.
     * The legacy `type`/`isShared` pair is the fallback.
     *
     * A block that matches none of them — an unknown or missing type — is not
     * rendered, as before.
     */
    public readonly isZeroBlock = isZeroBlock;
    public readonly isSpecificBlock = isSpecificBlock;
    public readonly isSharedBlock = isSharedBlock;

    /**
     * An ordinary block: the default subtype that is *not* the site-wide
     * header/footer, which is the same subtype and only differs by `meta`.
     */
    public isPlainBlock(block: DenormalizedBlockEntity): block is DenormalizedDefaultBlockEntity {
        return isDefaultBlock(block) && !blockIsShared(block);
    }
}
